/**
 * File Upload Store
 *
 * Manages state for file uploads, file list, and processing status.
 */

import { create } from 'zustand';
import {
  uploadFile,
  listFiles,
  getFile,
  processFile,
  deleteFile,
  getDownloadUrl,
  type UploadedFile,
  type FileUploadParams,
  type ProcessFileParams,
} from '../api/file-upload';
import { parseApiError, showErrorNotification } from '../utils/errorHandling';
import toast from 'react-hot-toast';

interface FileUploadState {
  // File list state
  files: UploadedFile[];
  selectedFile: UploadedFile | null;
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;

  // UI state
  isLoading: boolean;
  isUploading: boolean;
  uploadProgress: number;
  error: string | null;

  // Filters
  filters: {
    denomination_id?: string;
    file_type?: string;
    processing_status?: string;
    is_official?: boolean;
  };

  // Actions
  loadFiles: (reset?: boolean) => Promise<void>;
  uploadNewFile: (params: FileUploadParams) => Promise<UploadedFile | null>;
  selectFile: (file: UploadedFile | null) => void;
  refreshFile: (fileId: string) => Promise<void>;
  processSelectedFile: (params: ProcessFileParams) => Promise<void>;
  downloadFile: (fileId: string) => Promise<void>;
  deleteSelectedFile: () => Promise<void>;
  setFilters: (filters: Partial<FileUploadState['filters']>) => void;
  clearFilters: () => void;
  setOffset: (offset: number) => void;
  resetState: () => void;
}

const initialState = {
  files: [],
  selectedFile: null,
  total: 0,
  limit: 20,
  offset: 0,
  hasMore: false,
  isLoading: false,
  isUploading: false,
  uploadProgress: 0,
  error: null,
  filters: {},
};

export const useFileUploadStore = create<FileUploadState>((set, get) => ({
  ...initialState,

  /**
   * Load files with current filters and pagination
   */
  loadFiles: async (reset = false) => {
    const state = get();

    if (reset) {
      set({ offset: 0, files: [] });
    }

    set({ isLoading: true, error: null });

    try {
      const currentOffset = reset ? 0 : state.offset;
      const response = await listFiles(state.limit, currentOffset, state.filters);

      set({
        files: reset ? response.files : [...state.files, ...response.files],
        total: response.total,
        offset: currentOffset,
        hasMore: response.has_more,
        isLoading: false,
      });
    } catch (error) {
      const errorMessage = parseApiError(error);
      set({ error: errorMessage, isLoading: false });
      showErrorNotification('Failed to load files', errorMessage);
    }
  },

  /**
   * Upload a new file
   */
  uploadNewFile: async (params: FileUploadParams): Promise<UploadedFile | null> => {
    set({ isUploading: true, uploadProgress: 0, error: null });

    try {
      // Simulate upload progress (actual progress would need axios onUploadProgress)
      const progressInterval = setInterval(() => {
        set((state) => ({
          uploadProgress: Math.min(state.uploadProgress + 10, 90),
        }));
      }, 200);

      const uploadedFile = await uploadFile(params);

      clearInterval(progressInterval);
      set({ uploadProgress: 100 });

      // Add to the top of the list
      set((state) => ({
        files: [uploadedFile, ...state.files],
        total: state.total + 1,
        isUploading: false,
        uploadProgress: 0,
      }));

      toast.success(`File "${params.file.name}" uploaded successfully!`);
      return uploadedFile;
    } catch (error) {
      const errorMessage = parseApiError(error);
      set({ error: errorMessage, isUploading: false, uploadProgress: 0 });
      showErrorNotification('Upload failed', errorMessage);
      return null;
    }
  },

  /**
   * Select a file to view details
   */
  selectFile: (file: UploadedFile | null) => {
    set({ selectedFile: file });
  },

  /**
   * Refresh a specific file's details
   */
  refreshFile: async (fileId: string) => {
    try {
      const updatedFile = await getFile(fileId);

      // Update in the list
      set((state) => ({
        files: state.files.map((f) => (f.id === fileId ? updatedFile : f)),
        selectedFile: state.selectedFile?.id === fileId ? updatedFile : state.selectedFile,
      }));
    } catch (error) {
      const errorMessage = parseApiError(error);
      showErrorNotification('Failed to refresh file', errorMessage);
    }
  },

  /**
   * Process the selected file
   */
  processSelectedFile: async (params: ProcessFileParams) => {
    const { selectedFile } = get();
    if (!selectedFile) return;

    try {
      const updatedFile = await processFile(selectedFile.id, params);

      set((state) => ({
        files: state.files.map((f) => (f.id === selectedFile.id ? updatedFile : f)),
        selectedFile: updatedFile,
      }));

      toast.success('File processing started!');

      // Poll for status updates
      const pollInterval = setInterval(async () => {
        try {
          const refreshedFile = await getFile(selectedFile.id);

          set((state) => ({
            files: state.files.map((f) => (f.id === selectedFile.id ? refreshedFile : f)),
            selectedFile: state.selectedFile?.id === selectedFile.id ? refreshedFile : state.selectedFile,
          }));

          if (refreshedFile.processing_status === 'completed' || refreshedFile.processing_status === 'failed') {
            clearInterval(pollInterval);

            if (refreshedFile.processing_status === 'completed') {
              toast.success('File processing completed!');
            } else {
              toast.error('File processing failed');
            }
          }
        } catch (error) {
          clearInterval(pollInterval);
        }
      }, 3000); // Poll every 3 seconds

      // Stop polling after 5 minutes
      setTimeout(() => clearInterval(pollInterval), 300000);
    } catch (error) {
      const errorMessage = parseApiError(error);
      showErrorNotification('Failed to process file', errorMessage);
    }
  },

  /**
   * Download a file
   */
  downloadFile: async (fileId: string) => {
    try {
      const { download_url, filename } = await getDownloadUrl(fileId);

      // Open download URL in new tab
      const link = document.createElement('a');
      link.href = download_url;
      link.download = filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Download started!');
    } catch (error) {
      const errorMessage = parseApiError(error);
      showErrorNotification('Download failed', errorMessage);
    }
  },

  /**
   * Delete the selected file
   */
  deleteSelectedFile: async () => {
    const { selectedFile } = get();
    if (!selectedFile) return;

    try {
      await deleteFile(selectedFile.id);

      set((state) => ({
        files: state.files.filter((f) => f.id !== selectedFile.id),
        selectedFile: null,
        total: state.total - 1,
      }));

      toast.success('File deleted successfully!');
    } catch (error) {
      const errorMessage = parseApiError(error);
      showErrorNotification('Failed to delete file', errorMessage);
    }
  },

  /**
   * Update filters
   */
  setFilters: (filters: Partial<FileUploadState['filters']>) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
      offset: 0,
    }));
    // Reload with new filters
    get().loadFiles(true);
  },

  /**
   * Clear all filters
   */
  clearFilters: () => {
    set({ filters: {}, offset: 0 });
    get().loadFiles(true);
  },

  /**
   * Set pagination offset
   */
  setOffset: (offset: number) => {
    set({ offset });
  },

  /**
   * Reset state
   */
  resetState: () => {
    set(initialState);
  },
}));
